import {
  ChamberMemberProManualPaymentMethodTypeEnum,
  CmpInvoicePaymentRuleKindEnum,
  CmpInvoicePaymentRulesFieldsFragment,
  CmpStaffRecordablePaymentMethodsQuery,
  useCmpStaffRecordablePaymentMethodsQuery,
} from "../../../graphql/types";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../utils/userFacingError";

/**
 * One way money arrived that staff can record on an invoice. `id` is the
 * method type (sent as manualPaymentMethodId); `label` is the approved
 * dialog label; `recordLabel` is the chamber's saved label for that type,
 * sent where a mutation records the payment by label.
 */
export type StaffPaymentMethodOption = Readonly<{
  id: ChamberMemberProManualPaymentMethodTypeEnum;
  label: string;
  recordLabel: string;
}>;

export type StaffRecordablePaymentMethod =
  CmpStaffRecordablePaymentMethodsQuery["cmpStaffRecordablePaymentMethods"][number];

type StaffPaymentRules = Pick<
  CmpInvoicePaymentRulesFieldsFragment,
  "ruleKind"
> | null;

// Claude Design order and labels for the staff payment dialogs. Credit Card
// comes first where the dialog offers it; Invoice Me is never a received
// payment, so it is never listed.
const staffPaymentMethodDesignOrder: ReadonlyArray<
  Readonly<{ id: ChamberMemberProManualPaymentMethodTypeEnum; label: string }>
> = [
  { id: ChamberMemberProManualPaymentMethodTypeEnum.ETransfer, label: "E-Transfer" },
  { id: ChamberMemberProManualPaymentMethodTypeEnum.Cheque, label: "Cheque" },
  {
    id: ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit,
    label: "Direct Deposit",
  },
  { id: ChamberMemberProManualPaymentMethodTypeEnum.Cash, label: "Cash" },
];

/**
 * The received-payment methods one staff payment dialog offers for one
 * invoice. Staff record money the chamber already has, so the chamber's
 * customer-facing method switches do not apply (Ajax Oct 7 2026: "Company
 * Cheque" only, and staff could not record a direct deposit). Invoice rules
 * still apply: a group insurance invoice is confirmed by direct deposit only,
 * and monthly payment-plan installments are card only. Without invoice rules
 * the dialog cannot know what applies, so it offers no manual method.
 */
export const getStaffPaymentMethodOptions = ({
  paymentRules,
  recordableMethods,
}: {
  paymentRules: StaffPaymentRules | undefined;
  recordableMethods: readonly StaffRecordablePaymentMethod[];
}): StaffPaymentMethodOption[] => {
  if (
    !paymentRules ||
    paymentRules.ruleKind === CmpInvoicePaymentRuleKindEnum.MonthlyCreditCardOnly
  ) {
    return [];
  }

  const directDepositOnly =
    paymentRules.ruleKind ===
    CmpInvoicePaymentRuleKindEnum.InsuranceDirectDepositOnly;
  const recordLabelById = new Map(
    recordableMethods.map((method) => [method.id, method.label.trim()]),
  );

  return staffPaymentMethodDesignOrder
    .filter(
      ({ id }) =>
        recordLabelById.has(id) &&
        (!directDepositOnly ||
          id === ChamberMemberProManualPaymentMethodTypeEnum.DirectDeposit),
    )
    .map(({ id, label }) => ({
      id,
      label,
      recordLabel: recordLabelById.get(id) || label,
    }));
};

/** Loads the backend list of methods staff can record for the chamber. */
export const useStaffRecordablePaymentMethods = ({
  chamberId,
  skip = false,
}: {
  chamberId: string;
  skip?: boolean;
}) => {
  const { data, error, loading } = useCmpStaffRecordablePaymentMethodsQuery({
    fetchPolicy: "cache-first",
    skip: skip || !chamberId,
    variables: { chamberId },
    // A failed load shows in the payment pop-up, not the full-page error.
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });

  return {
    error,
    // Text for the payment pop-up when the method list could not load.
    errorText: error
      ? getUserFacingErrorText(
          error,
          buildStaffErrorFallback("load the payment methods"),
        )
      : null,
    loading,
    recordableMethods: data?.cmpStaffRecordablePaymentMethods || [],
  };
};
